import { useRef, useState, useMemo, useEffect } from 'react';
import { Prompt, PromptGroup_deprecated } from '@server/prompts';
import { Stack } from '@mui/material';
import { PromptsLibraryDialog } from '../../promptsLibrary__deprecated/PromptsLibraryDialog';
import { PromptsLibraryV2Dialog } from '../PromptsLibraryV2Dialog';
import { PromptGroupMinimizedPopover } from './PromptGroupMinimizedPopover';
import {
  selectPromptGroups,
  selectPrompts,
  selectPromptGroupsWithPrompts_deprecated,
  selectIsLoadingPrompts,
} from '../../../../reducers/promptLibraryReducer';
import { useSelector } from 'react-redux';
import { AI_AGENTS_EVENTS } from '../../../../services/events';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { usePrompt } from '../usePrompt';
import { MinimizedPromptCard } from './MinimizedPromptCard';

interface PromptGroupsMinimizedProps {
  onPromptClick: (promptTitle: string) => Promise<void>;
}

export const PromptGroupsMinimized = ({ onPromptClick }: PromptGroupsMinimizedProps) => {
  const promptGroupsV2 = useSelector(selectPromptGroups);
  const promptsFromStore = useSelector(selectPrompts);
  const promptGroupsV1 = useSelector(selectPromptGroupsWithPrompts_deprecated);
  const isLoadingPrompts = useSelector(selectIsLoadingPrompts);
  const [openPromptsLibraryDialog, setOpenPromptsLibraryDialog] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const sendBiEvent = useSendBiEvent();
  const { promptsLibraryV2 } = useFeatureToggle();
  const { loadPrompts } = usePrompt();

  useEffect(() => {
    if (promptsLibraryV2 && selectedGroupId && !promptsFromStore) {
      loadPrompts();
    }
  }, [promptsLibraryV2, selectedGroupId, promptsFromStore, loadPrompts]);

  //TODO fix with SWEEP-8588
  const allPromptGroups = promptsLibraryV2 ? promptGroupsV2 : promptGroupsV1;

  const minimizedPromptGroups =
    allPromptGroups?.filter((promptGroup) => promptGroup.isPinned) ?? [];

  //TODO fix with SWEEP-8588
  const selectedGroupPrompts = useMemo((): Prompt[] => {
    if (!promptsLibraryV2 || !selectedGroupId || !promptsFromStore) return [];
    return promptsFromStore.filter((p) => p.promptGroupId === selectedGroupId);
  }, [promptsLibraryV2, selectedGroupId, promptsFromStore]);

  const selectedGroup = useMemo(() => {
    if (!selectedGroupId) return undefined;

    if (promptsLibraryV2) {
      return promptGroupsV2?.find((g) => g.id === selectedGroupId);
    }

    const promptGroupIndex = promptGroupsV1?.findIndex((g) => g.id === selectedGroupId);
    return promptGroupIndex !== undefined && promptGroupIndex !== -1
      ? promptGroupsV1?.[promptGroupIndex]
      : undefined;
  }, [selectedGroupId, promptGroupsV1, promptGroupsV2, promptsLibraryV2]);

  const renderPromptsLibraryDialog = () => {
    if (!openPromptsLibraryDialog) return null;

    if (promptsLibraryV2) {
      return (
        <PromptsLibraryV2Dialog
          open={openPromptsLibraryDialog}
          onClose={() => setOpenPromptsLibraryDialog(false)}
        />
      );
    }

    return (
      <PromptsLibraryDialog
        open={openPromptsLibraryDialog}
        onClose={() => setOpenPromptsLibraryDialog(false)}
        onPromptClick={onPromptClick}
      />
    );
  };

  return (
    <>
      {renderPromptsLibraryDialog()}

      {selectedGroup && (
        <PromptGroupMinimizedPopover
          group={selectedGroup}
          prompts={
            //TODO fix with SWEEP-8588
            promptsLibraryV2
              ? selectedGroupPrompts
              : ((selectedGroup as PromptGroup_deprecated).prompts ?? [])
          }
          isLoading={promptsLibraryV2 && isLoadingPrompts}
          onClose={() => setSelectedGroupId(null)}
          onPromptClick={async (promptText: string) => {
            sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryPinnedPromptClick });
            await onPromptClick(promptText);
          }}
          anchorEl={parentRef.current}
        />
      )}

      <Stack
        direction="row"
        gap={1}
        flexWrap="wrap"
        justifyContent="center"
        alignItems="center"
        ref={parentRef}
      >
        {minimizedPromptGroups.map((promptGroup) => (
          <MinimizedPromptCard
            key={promptGroup.id}
            promptGroupId={promptGroup.id}
            name={promptGroup.name}
            icon={promptGroup.iconName ?? ''}
            onClick={() => {
              sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryPinnedPromptCategoryClick });
              setSelectedGroupId(promptGroup.id);
            }}
          />
        ))}

        {/* by product definition: always show 'more' to allow access to library */}
        <MinimizedPromptCard
          key="more"
          promptGroupId="more"
          name="Prompts Library"
          icon="Plus"
          onClick={() => {
            sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryOpen });
            setOpenPromptsLibraryDialog(true);
          }}
        />
      </Stack>
    </>
  );
};
